import type { World } from '@/core/ecs/World'
import { ArenaResource } from '@/game/arenas/Arena'
import { arenaById, scaledArena } from '@/game/arenas'
import { Events } from '@/game/events'
import { EventsResource, MatchResource } from '@/game/resources'
import type { SceneBridge } from '@/gfx/SceneBridge'
import type { EventType } from '@/core/EventBus'
import type { SnapshotMessage } from './protocol'
import { Replica } from './replica'
import { SnapshotBuffer } from './SnapshotBuffer'

const EVENT_TYPES = Events as Record<string, EventType<object>>

export interface PlaybackStep {
  /** Interpolation factor for this rendered frame; the render sync's alpha. */
  alpha: number
  /** A new match began: the platform has to be rebuilt. */
  newMatch: boolean
  /** A new round began: the platform's fallen slabs come back. */
  newRound: boolean
}

/**
 * Plays the server's snapshots back into a local world.
 *
 * Presentation then runs on that world exactly as it runs offline: the
 * replica has the entities and components, the match and arena state are the
 * server's, and each event reaches the local queue when the playback clock
 * reaches the moment it happened — so a sound lands on the frame that shows
 * its cause, not a snapshot early.
 */
export class NetPlayback {
  readonly buffer = new SnapshotBuffer()
  readonly replica: Replica
  private shown: SnapshotMessage | null = null

  constructor(
    private readonly world: World,
    bridge: SceneBridge | null,
  ) {
    this.replica = new Replica(world, bridge)
  }

  receive(snap: SnapshotMessage, localNow: number): void {
    this.buffer.push(snap, localNow)
  }

  /** Brings the world to what should be drawn at `localNow`. */
  advance(localNow: number): PlaybackStep {
    const frame = this.buffer.frame(localNow)
    const step: PlaybackStep = { alpha: 1, newMatch: false, newRound: false }
    if (!frame) return step
    step.alpha = frame.alpha

    const { from, to } = frame
    if (to !== this.shown) {
      step.newMatch = this.shown === null || this.shown.matchId !== to.matchId
      step.newRound = step.newMatch || this.shown!.match.roundIndex !== to.match.roundIndex
      if (step.newMatch) this.replica.reset()
      this.replica.apply(from && from.matchId === to.matchId ? from.entities : null, to.entities)
      this.applyState(to, step.newMatch)
      this.shown = to
    }

    const events = this.world.resource(EventsResource)
    for (const event of this.buffer.takeEvents()) {
      const type = EVENT_TYPES[event.kind]
      if (type) events.emit(type, this.replica.event(event))
    }
    return step
  }

  /** Forgets everything, as when leaving a room. */
  reset(): void {
    this.buffer.clear()
    this.replica.reset()
    this.shown = null
  }

  private applyState(snap: SnapshotMessage, newMatch: boolean): void {
    const match = this.world.resource(MatchResource)
    Object.assign(match, snap.match)
    match.players = snap.match.players.map((id) => this.replica.entity(id))

    const arena = this.world.resource(ArenaResource)
    if (newMatch) arena.reset(scaledArena(arenaById(snap.match.arenaId), snap.match.arenaScale))
    arena.scale = snap.arena.scale
    arena.collapsing = snap.arena.collapsing
    arena.warning = snap.arena.warning
  }
}
